import { useCallback } from "react";
import { useNetworkFormContext } from "../../context/NetworkFormContext";
import {
  CheckboxProps,
  DropdownProps,
  InputOnChangeData,
} from "semantic-ui-react";
import { getDefaultNameservers } from "../helpers/helper";
import { AllocationPool } from "../../types/allocationPool";
import { HostRoute } from "../../types/hostRoute";
import { SubnetItemProps } from "../SubnetItem";

const useSubnetItem = ({ index, onChange, nameservers }: SubnetItemProps) => {
  const {
    formData: { region, subnets },
    predefined_params,
  } = useNetworkFormContext();

  const subnet = subnets[index];

  const handleInputChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>, data: InputOnChangeData) => {
      const { name, value } = data;
      const updated = { ...subnet, [name]: value };
      onChange(index, updated);
    },
    [subnet, index, onChange],
  );

  const handleSelectChange = useCallback(
    (e: React.FormEvent<HTMLElement>, data: DropdownProps) => {
      const { name, value } = data;
      const updated = { ...subnet, [name]: value };
      onChange(index, updated);
    },
    [subnet, index, onChange],
  );

  const handleCheckboxChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
      const { name, value, checked } = data;
      if (!name) return;

      if (name.startsWith("ipVersion")) {
        const defaultNameservers = getDefaultNameservers({
          nameservers,
          ipVersion: Number(value),
          region: region || predefined_params?.region,
        });
        const updated = {
          ...subnet,
          ipVersion: Number(value),
          dnsNameservers: defaultNameservers,
        };
        onChange(index, updated);
      } else {
        const updated = { ...subnet, [name]: Number(value) || checked };
        onChange(index, updated);
      }
    },
    [index, onChange, subnet, nameservers, predefined_params?.region, region],
  );

  const onAllocationPoolsChange = useCallback(
    (allocationPools: AllocationPool[]) => {
      const updated = { ...subnet, allocationPools };
      onChange(index, updated);
    },
    [subnet, index, onChange],
  );

  const onHostRoutesChange = useCallback(
    (hostRoutes: HostRoute[]) => {
      const updated = { ...subnet, hostRoutes };
      onChange(index, updated);
    },
    [subnet, index, onChange],
  );

  return {
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    onAllocationPoolsChange,
    onHostRoutesChange,
    subnet,
    subnetsCount: subnets.length,
  };
};

export default useSubnetItem;
